/// <reference types="vite/client" />
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import NiceModal from "@ebay/nice-modal-react";
import { RecentRoutesProvider } from "@hooks/useRecentRoutes.tsx";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactFlowProvider } from "@xyflow/react";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { getApiErrorMessage } from "@utils/apiErrorMessage";

import { TooltipProvider } from "@components/shadcn/tooltip.tsx";

import Auth0ProviderWithHistory from "@components/authentication/Auth0ProviderWithHistory.tsx";
import AuthedElement from "@components/authentication/AuthedElement.tsx";
import { TutorialProvider } from "@components/tutorials/TutorialContext.tsx";
import ErrorHandler from "@components/util/ErrorHandler.tsx";
import StompSessionProviderWithAuth from "@components/websocket/StompSessionProviderWithAuth.tsx";

import App from "./App.tsx";
import "./index.css";
import { bootstrapLaunchDarkly } from "./launchDarkly/bootstrapLaunchDarkly.tsx";

const hostname = window.location.hostname;

let environment = "local";
if (hostname === "atlas.magmutual.com") {
  environment = "prod";
} else if (hostname === "atlas.dev.magmutual.com") {
  environment = "dev";
} else if (hostname === "atlas.qa.magmutual.com") {
  environment = "qa";
} else if (hostname === "atlas.uat.magmutual.com") {
  environment = "uat";
} else if (hostname.endsWith(".core-tasks.pages.dev")) {
  environment = hostname.replace(".core-tasks.pages.dev", "").toLowerCase();
}

// Only initialize Datadog RUM in non-local environments
if (environment !== "local") {
  datadogRum.init({
    applicationId: "3b627420-dec2-461b-a6bb-671027bc1d03",
    clientToken: "pub5dd8f78a89075560123624cf3c731c05",
    site: "us5.datadoghq.com",
    service: "core-tasks-web",
    env: environment,

    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    plugins: [reactPlugin({ router: true })],
    allowedTracingUrls: [(url) => url.startsWith("https://core-tasks")],
  });
}

const sendErrorNotification = (error: unknown) =>
  toast.error(
    getApiErrorMessage(error, "An error occurred. Please try again."),
  );

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      //staleTime: 5 * 1000 * 60, // 5 minutes
      staleTime: Infinity,
      // suspense: true,
    },
    mutations: {
      throwOnError: false,
      retry: 3,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const meta = mutation.options.meta as
        | { skipGlobalErrorNotification?: boolean }
        | undefined;
      if (meta?.skipGlobalErrorNotification) {
        return;
      }
      sendErrorNotification(error);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      const meta = query.meta as
        | { skipGlobalErrorNotification?: boolean }
        | undefined;
      if (meta?.skipGlobalErrorNotification) {
        return;
      }
      sendErrorNotification(error);
    },
  }),
});

void (async () => {
  const { LaunchDarklyProvider, launchDarklyDevToolbarSlot } =
    await bootstrapLaunchDarkly();

  const rootElement = (
    <StrictMode>
      <ErrorHandler>
        <BrowserRouter>
          <ReactFlowProvider>
            <Auth0ProviderWithHistory>
              <QueryClientProvider client={queryClient}>
                <LaunchDarklyProvider>
                  {launchDarklyDevToolbarSlot}
                  <TooltipProvider delayDuration={300}>
                    <StompSessionProviderWithAuth>
                      <TutorialProvider>
                        <NiceModal.Provider>
                          <RecentRoutesProvider>
                            <Toaster />
                            <Routes>
                              <Route
                                path="/*"
                                element={<AuthedElement element={App} />}
                              />
                            </Routes>
                          </RecentRoutesProvider>
                        </NiceModal.Provider>
                      </TutorialProvider>
                    </StompSessionProviderWithAuth>
                  </TooltipProvider>
                </LaunchDarklyProvider>
              </QueryClientProvider>
            </Auth0ProviderWithHistory>
          </ReactFlowProvider>
        </BrowserRouter>
      </ErrorHandler>
    </StrictMode>
  );

  ReactDOM.createRoot(document.getElementById("root")).render(rootElement);
})();
